// Rings.tsx — A planet's debris ring (moons disrupted inside the Roche limit): a
// flat, tilted annulus lit by the star. Rendered inside the planet body group so
// it scales with the planet's apparent size; doesn't spin with the surface. The
// planet's sphere casts an analytic shadow across the ring (see shadow.ts), and a
// per-fragment radial profile gives it concentric density bands + Cassini-style
// gaps instead of a flat painted disk.
import { useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { DoubleSide, Vector3 } from 'three';
import type { WebGLProgramParametersWithUniforms } from 'three';
import type { PlanetRings } from '../types';
import { makeShadowUniforms, setOccluders, standardShadowPatch } from './shadow';
import type { Occluder, ShadowUniforms } from './shadow';
import { FBM3D, SIMPLEX3D } from './planet/glslNoise';

// Shadow patch + a radial density profile across the annulus: fine concentric
// banding, broad brightness variation, and two sharp gaps (Cassini divisions),
// with the inner/outer edges feathered so the ring doesn't read as a hard cut-out.
function ringSurfacePatch(shadow: ShadowUniforms, inner: number, outer: number) {
  const base = standardShadowPatch(shadow);
  return (shader: WebGLProgramParametersWithUniforms) => {
    base(shader);
    shader.uniforms.uInner = { value: inner };
    shader.uniforms.uOuter = { value: outer };
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec2 vRingXY;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\n  vRingXY = position.xy;');
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
        varying vec2 vRingXY;
        uniform float uInner;
        uniform float uOuter;
        ${SIMPLEX3D}
        ${FBM3D}`,
      )
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        {
          float t = clamp((length(vRingXY) - uInner) / max(uOuter - uInner, 1e-4), 0.0, 1.0);
          float bands = fbm(vec3(t * 22.0, 1.7, 0.0));
          float coarse = fbm(vec3(t * 5.0, 9.0, 0.0));
          float density = mix(0.4, 1.0, bands) * mix(0.55, 1.0, coarse);
          density *= smoothstep(0.015, 0.05, abs(t - 0.45)); // Cassini gap
          density *= smoothstep(0.015, 0.045, abs(t - 0.72)); // Encke-ish gap
          density *= smoothstep(0.0, 0.04, t) * smoothstep(0.0, 0.06, 1.0 - t);
          diffuseColor.rgb *= 0.7 + 0.5 * bands;
          diffuseColor.a *= density;
        }`,
      );
  };
}

interface Props {
  rings: PlanetRings;
  sunWorldPos: Vector3;
  sunRadius: number;
  /** The parent planet's sphere — casts a shadow band across the ring. */
  occluder: Occluder;
}

export function Rings({ rings, sunWorldPos, sunRadius, occluder }: Props) {
  const shadow = useMemo(makeShadowUniforms, []);
  const onBeforeCompile = useMemo(
    () => ringSurfacePatch(shadow, rings.innerRadius, rings.outerRadius),
    [shadow, rings.innerRadius, rings.outerRadius],
  );
  const occList = useMemo(() => [occluder], [occluder]);

  useFrame(() => {
    setOccluders(shadow, sunWorldPos, sunRadius, occList);
  });

  return (
    <group rotation={[Math.PI / 2 + rings.tilt, 0, 0]}>
      {/* renderOrder 4 (after the surface/ocean/cloud-shadow/cloud/atmosphere shells) +
          depthWrite OFF: a transparent ring must NOT write depth, or it punches a hole in
          the cloud/ocean/atmosphere shells behind it (they'd fail the depth test and the
          clouds vanish where a ring crosses). depthTest stays on, so the planet still
          occludes the ring's far half; the near half draws over the clouds, semi-clear. */}
      <mesh renderOrder={4}>
        <ringGeometry args={[rings.innerRadius, rings.outerRadius, 180, 6]} />
        <meshStandardMaterial
          color={rings.color}
          side={DoubleSide}
          transparent
          opacity={0.85}
          depthWrite={false}
          roughness={1}
          metalness={0}
          onBeforeCompile={onBeforeCompile}
        />
      </mesh>
    </group>
  );
}
